<script setup lang="ts">
import { X, Loader2, Scale, ExternalLink, ArrowRight } from 'lucide-vue-next'

const props = defineProps<{
  normId?: string | null
  sectionId: string
}>()

// `lawRequest` carries the chat back-reference (A5) that goToFullLaw forwards to the full-page law
// view. The inline panel itself has no "back" affordance — the X (closeLaw) already returns to chat.
const { closeLaw, lawRequest } = usePanelLayout()
const { apiFetch } = useApi()

// Declared once in `shared/laws/normDetail.ts` and `shared/laws/lawSearch.ts`, which `getLawDetail`
// and `LawService` are annotated with. This component kept its own narrower `LawNorm` /
// `LawSection` / `ReadSectionResult` until 2026-08-26; all three were subsets of what the endpoints
// return, so nothing here changes except that a projection change now breaks the build.
import type { LawSectionTexts, NormDetail } from '#shared/laws/normDetail'
import type { LawReadResponse, LawSectionSearchResult } from '#shared/laws/lawSearch'
// The law page's renderer, not a copy of it: an article opened from chat must read exactly as it
// does on the law page, PDF artifacts stripped and chapter markers as headings (MAR-957).
import { renderSectionHtml } from '~/lib/laws/sectionHtml'

const norm = ref<NormDetail | null>(null)
const sectionText = ref<string | null>(null)
const loading = ref(true)

const matchedSection = computed(() => {
  if (!norm.value || !props.sectionId) return null
  return norm.value.sections.find(section => section.id === props.sectionId) || null
})

watch(() => [props.normId, props.sectionId] as const, loadLawSection, { immediate: true })

async function loadLawSection() {
  loading.value = true
  norm.value = null
  sectionText.value = null
  try {
    let normId = props.normId || null
    let readSection: LawSectionSearchResult | null = null

    if (!normId && props.sectionId) {
      const read = await apiFetch<LawReadResponse>('/api/legal/read', {
        method: 'POST',
        body: { ids: [props.sectionId] },
      })
      readSection = read.results?.[0] || null
      normId = readSection?.norm_id || null
    }

    if (!normId) return
    norm.value = await apiFetch<NormDetail>(`/api/laws/${normId}`)
    const section = matchedSection.value
    if (section) {
      const texts = await apiFetch<LawSectionTexts>(
        `/api/laws/${normId}/sections?ids=${section.id}`,
      )
      sectionText.value = texts[section.id] ?? readSection?.full_text ?? null
    } else if (readSection) {
      sectionText.value = readSection.full_text
    }
  } catch {
    norm.value = null
  } finally {
    loading.value = false
  }
}

function goToFullLaw() {
  if (!norm.value || !matchedSection.value) return
  const query: Record<string, string> = { section: matchedSection.value.id }
  // Carry the chat back-reference (A5) so the full-page law view can offer "Volver a la respuesta".
  const src = lawRequest.value?.source
  if (src?.conversationId) {
    query.fromChat = src.conversationId
    if (src.messageId) query.msg = src.messageId
  }
  closeLaw()
  navigateTo({ path: `/app/laws/id/${norm.value.id}`, query })
}
</script>

<template>
  <div class="flex flex-col h-full bg-background">
    <!-- Header (the X closes the panel back to full-width chat — no separate "back" affordance). -->
    <div class="px-3 py-2 border-b shrink-0">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2 min-w-0 flex-1">
          <Scale class="h-4 w-4 shrink-0 text-primary" />
          <span v-if="norm" class="text-sm font-medium truncate">{{norm.title }}</span>
          <span v-else class="text-sm text-muted-foreground">Loading...</span>
        </div>
        <button
          type="button"
          class="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent-lighter transition-colors shrink-0"
          @click="closeLaw"
        >
          <X class="h-4 w-4" />
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex-1 flex items-center justify-center">
      <Loader2 class="h-5 w-5 animate-spin text-muted-foreground" />
    </div>

    <!-- Section content -->
    <div v-else-if="norm && matchedSection && sectionText" class="flex-1 overflow-y-auto">
      <div class="px-4 py-4">
        <div class="flex items-center gap-2 mb-3">
          <Scale class="h-3.5 w-3.5 text-primary shrink-0" />
          <h2 class="text-sm font-semibold">{{ matchedSection.section_number }}</h2>
        </div>
        <div
          class="text-[13px] text-muted-foreground leading-relaxed"
          v-html="renderSectionHtml(sectionText, norm?.jurisdiction)"
        />
        <div class="mt-4 pt-3 border-t space-y-2">
          <button
            type="button"
            :aria-label="`Ver ley completa: ${norm.title}`"
            class="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-md border border-border bg-background hover:bg-accent-lighter text-primary text-base font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            @click="goToFullLaw"
          >
            <Scale class="h-4 w-4" />
            Ver ley completa
            <ArrowRight class="h-3.5 w-3.5" />
          </button>
          <div class="flex items-center justify-between gap-2">
            <p class="text-[10px] text-muted-foreground truncate">{{ norm.title }}</p>
            <a
              v-if="norm.source_url"
              :href="norm.source_url"
              target="_blank"
              class="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors shrink-0"
            >
              <ExternalLink class="h-3 w-3" />
              PDF
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Norm loaded but section not found -->
    <div v-else-if="norm" class="flex-1 overflow-y-auto">
      <div class="px-4 py-4">
        <p class="text-sm font-medium mb-2">{{ norm.title }}</p>
        <p class="text-xs text-muted-foreground">{{ norm.sections.length }} artículos</p>
      </div>
    </div>
  </div>
</template>
